import { useEffect, useState } from "react";

import CustomGrid from "../CustomGrid";
import { gridStructure } from "constants/CustomGridStructure";

import { Box, Paper } from "@mui/material";

import { GridRowSelectionModel } from "@mui/x-data-grid";
import { EnhancedTableToolbar } from "../EnhancedToolbar";

import { User } from "types";
import { USERS_URL as BASE_URL } from "constants/urls";

import { ENTITY } from "constants/entities";

import Loader from "../Loader";
import useModals from "hooks/useModals";
import useCrud from "hooks/useCrud";
import AddUserModal from "./AddUserModal";
import EditUserModal from "./EditUserModal";
import { useSession } from "providers/SessionProvider";
import { Roles, STATUS_CODES } from "constants/index";
import { useToast } from "hooks/useToast";
import { useFilter } from "hooks/useFilter";
import DeleteConfirmationModal from "components/utils/DeleteConfirmationModal";
import { exportToExcel } from "helpers/exportToExcel";

type UserUpdatePayload = Pick<
  User,
  | "id"
  | "first_name"
  | "last_name"
  | "email"
  | "role"
  | "is_active"
  | "client_id"
> &
  Partial<Pick<User, "password">>;

export default function UserList() {
  const [selected, setSelected] = useState<GridRowSelectionModel>([]);

  const {
    addModalOpen,
    setAddModalOpen,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
  } = useModals();

  const { showSnackBar } = useToast();

  const {
    loading,
    firstLoad,
    updateData: updateUser,
    createData: createUser,
    deleteData: deleteUser,
    showToast,
    toastMessage,
    toastSeverity,
    setShowToast,
  } = useCrud<User>(BASE_URL);

  const {
    users,
    clients,
    addSessionData,
    updateSessionData,
    deleteSessionData,
    userData,
  } = useSession();

  const {
    filterText,
    setFilterText,
    filteredData: filteredUsers,
  } = useFilter<User>({
    data: users,
    fields: gridStructure[ENTITY.USERS].fields,
  });

  const userExcelColumns = {
    "Nombre": (u: User) => `${u.first_name ?? ""}`.trim(),
    "Apellido": (u: User) => `${u.last_name ?? ""}`.trim(),
    "Email": (u: User) => u.email ?? "-",
    "Rol": (u: User) => u.role ?? "-",
    "Activo": (u: User) => (u.is_active ? "SI" : "NO"),
  } satisfies Record<string, (row: User) => any>;

  const handleExportUsersExcel = () => {
    exportToExcel<User>({
      data: filteredUsers,
      columns: userExcelColumns,
      sheetName: "Usuarios",
      fileNamePrefix: "Usuarios",
      headerRows: [
        ["Listado de Usuarios"],
        [`Filtro: ${filterText?.trim() ? filterText : "—"}`],
        [],
      ],
    });
  };

  const handleDeleteSelected = async () => {
    if (selected.includes(userData?.id || 0)) {
      showSnackBar("No se puede eliminar el mismo usuario.", "error");
      return;
    }

    const failedDeletes: number[] = [];

    for (const id of selected) {
      const reponse = await deleteUser(id, true, true);
      if (reponse.status !== STATUS_CODES.OK) {
        failedDeletes.push(Number(id));
      }
    }
    deleteSessionData(
      ENTITY.USERS,
      selected
        .filter((item) => !failedDeletes.includes(Number(item)))
        .map((item) => item.toString())
    );

    setSelected([]);
  };

  const handleCreate = async (newUser: User) => {
    const response = await createUser(newUser, true, true);
    if (response.status === STATUS_CODES.OK) {
      addSessionData(ENTITY.USERS, response.data as User);
    }
  };

  const buildUserUpdatePayload = (user: User): UserUpdatePayload => ({
    id: user.id,
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    role: user.role,
    is_active: user.is_active,
    client_id: user.role === Roles.CLIENT ? user.client_id : null,
    ...(user.password ? { password: user.password } : {}),
  });

  const handleEdit = async (userToEdit: User) => {
    const payload = buildUserUpdatePayload(userToEdit);
    const response = await updateUser(userToEdit.id, payload as User, true, true);
    if (response.status === STATUS_CODES.OK && response.data) {
      updateSessionData(ENTITY.USERS, response.data as User);
    }
    setSelected([]);
  };

  useEffect(() => {
    if (showToast) {
      showSnackBar(toastMessage, toastSeverity);
      setShowToast(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast]);

  return (
    <>
      {firstLoad ? (
        <Loader isFirstLoad={firstLoad} isLoading={loading} />
      ) : (
        <>
          <Loader isFirstLoad={firstLoad} isLoading={loading} />
          <AddUserModal
            open={addModalOpen}
            handleOpen={() => setAddModalOpen(!addModalOpen)}
            onCreate={handleCreate}
            clients={clients}
            users={users}
          />
          <DeleteConfirmationModal
            open={deleteModalOpen}
            handleOpen={() => setDeleteModalOpen(!deleteModalOpen)}
            entityName={ENTITY.USERS}
            values={selected.map((item) => {
              const user = users.find((user) => user.id === item);
              if (user) return user.first_name + " " + user.last_name;
              return "";
            })}
            handleDelete={handleDeleteSelected}
          />
          {selected.length > 0 ? (
            <EditUserModal
              open={editModalOpen}
              handleOpen={() => setEditModalOpen(!editModalOpen)}
              value={users.find((user) => user.id === selected[0]) as User}
              onEdit={handleEdit}
              clients={clients}
              users={users}
            />
          ) : null}
          <Box sx={{ width: "99%" }}>
            <Paper sx={{ width: "100%", mb: 2 }}>
              <EnhancedTableToolbar
                numSelected={selected.length}
                handleAddModalOpen={() => setAddModalOpen(!addModalOpen)}
                handleEditModalOpen={() => setEditModalOpen(!editModalOpen)}
                handleDeleteModalOpen={() =>
                  setDeleteModalOpen(!deleteModalOpen)
                }
                onFilterChange={setFilterText}
                onExportExcel={handleExportUsersExcel}
              />
              <CustomGrid
                structure={gridStructure[ENTITY.USERS]}
                rows={filteredUsers}
                onEdit={handleEdit}
                selectionModel={selected}
                onRowSelection={setSelected}
              />
            </Paper>
          </Box>
        </>
      )}
    </>
  );
}
